import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { CATEGORIES, STATUSES, ACTION_TYPES, IMPACTS, US_STATES, formatLabel } from "@/lib/api";
import { useCoverage } from "@/hooks/usePublicConfig";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Sparkles, ShieldCheck, Send, Globe, EyeOff, Mail } from "lucide-react";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import AIDraftAssistant from "@/components/admin/AIDraftAssistant";
import CitationsEditor from "@/components/admin/CitationsEditor";
import QuickAddSource from "@/components/admin/QuickAddSource";

const empty = {
  title: "", summary: "", body: "",
  category: "reproductive", action_type: "bill", status: "proposed", impact: "restricts",
  jurisdiction_level: "federal", jurisdiction: "Federal", country: "US",
  date_action: new Date().toISOString().slice(0, 10),
  citations: [], tags: [], actors: [],
  ai_assisted: false,
  review_state: "draft",
};

const STATE_STYLE = {
  draft: "bg-[#EAEAEA] text-black",
  pending_review: "bg-[#FFE08A] text-black",
  published: "bg-[#008A00] text-white",
};

export default function AdminEntryForm() {
  const { levels: JURISDICTION_LEVELS, countries: COUNTRIES } = useCoverage();
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [aiUrl, setAiUrl] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const config = usePublicConfig();
  const editorEmail = config.editorial_contact_email;
  const PUBLIC_URL = process.env.REACT_APP_PUBLIC_URL || (typeof window !== "undefined" ? window.location.origin : "");

  const reload = useCallback(async () => {
    if (!id) return;
    try {
      const r = await api.get(`/admin/entries/${id}`);
      setForm({ ...empty, ...r.data });
    } catch { toast.error("Failed to load entry"); }
  }, [id]);
  useEffect(() => { reload(); }, [reload]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (id) {
        await api.put(`/entries/${id}`, form);
        toast.success("Saved");
        await reload();
      } else {
        const res = await api.post("/entries", form);
        toast.success("Draft created.");
        navigate(`/admin/edit/${res.data.id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setLoading(false); }
  };

  const aiSummarize = async () => {
    if (!aiUrl) return;
    setAiLoading(true);
    try {
      const res = await api.post("/ai/summarize-url", { url: aiUrl });
      const d = res.data;
      const cite = d.suggested_citation;
      setForm((f) => ({
        ...f,
        title: d.title || f.title,
        summary: d.summary || f.summary,
        category: d.category || f.category,
        action_type: d.action_type || f.action_type,
        status: d.status || f.status,
        impact: d.impact || f.impact,
        jurisdiction_level: d.jurisdiction_level || f.jurisdiction_level,
        jurisdiction: d.jurisdiction || f.jurisdiction,
        country: d.country || f.country,
        date_action: d.date_action || f.date_action,
        tags: Array.isArray(d.tags) ? d.tags : f.tags,
        actors: Array.isArray(d.actors) ? d.actors : f.actors,
        citations: cite ? [{ ...cite, verified: false }, ...f.citations] : f.citations,
        ai_assisted: true,
      }));
      toast.success("AI draft populated. Verify each citation before publishing.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI summarization failed");
    } finally { setAiLoading(false); }
  };

  const addCitation = () => upd("citations", [...form.citations, { _cid: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `c-${Date.now()}-${Math.random()}`, title: "", url: "", publisher: "", date: "", quote: "", verified: false }]);
  const updCitation = (i, k, v) => upd("citations", form.citations.map((c, j) => j === i ? { ...c, [k]: v } : c));
  const rmCitation = (i) => upd("citations", form.citations.filter((_, j) => j !== i));

  const verifyCitation = async (i) => {
    if (!id) {
      toast.error("Save the draft first, then verify citations.");
      return;
    }
    try {
      const r = await api.post(`/entries/${id}/citations/${i}/verify`);
      setForm({ ...empty, ...r.data });
      toast.success("Citation marked verified");
    } catch (e) { toast.error(e?.response?.data?.detail || "Verify failed"); }
  };

  const unverifyCitation = async (i) => {
    if (!id) return;
    try {
      const r = await api.post(`/entries/${id}/citations/${i}/unverify`);
      setForm({ ...empty, ...r.data });
      toast.success("Citation unverified");
    } catch { toast.error("Unverify failed"); }
  };

  const submitForReview = async () => {
    if (!id) return;
    try {
      await api.post(`/entries/${id}/submit-for-review`);
      toast.success("Submitted for review");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const publish = async () => {
    if (!id) return;
    try {
      await api.post(`/entries/${id}/publish`);
      toast.success("Published. The entry is now public.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot publish — verify all citations first."); }
  };

  const unpublish = async () => {
    if (!id || !window.confirm("Unpublish this entry? It returns to drafts and is hidden from public.")) return;
    try {
      await api.post(`/entries/${id}/unpublish`);
      toast.success("Unpublished");
      reload();
    } catch { toast.error("Failed"); }
  };

  const cites = form.citations || [];
  const verifiedCount = cites.filter((c) => c.verified).length;
  const canPublish = cites.length > 0 && verifiedCount === cites.length && id;

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-xs uppercase tracking-wider hover:underline" data-testid="admin-back">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="flex flex-wrap items-end justify-between gap-3 mt-4">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter">{id ? "Edit Entry" : "New Draft"}</h1>
          {id && (
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <span className={`font-mono-plex text-[11px] uppercase tracking-[0.25em] px-2 py-1 ${STATE_STYLE[form.review_state]}`} data-testid="entry-state-badge">
                {formatLabel(form.review_state || "draft")}
              </span>
              {form.ai_assisted && (
                <span className="font-mono-plex text-[11px] uppercase tracking-[0.25em] text-[#0050FF] inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" /> AI-assisted draft
                </span>
              )}
              {form.created_by && (
                <span className="font-mono-plex text-[11px] uppercase tracking-[0.25em] text-[#555]">drafted by {form.created_by}</span>
              )}
              {form.reviewed_by && (
                <span className="font-mono-plex text-[11px] uppercase tracking-[0.25em] text-[#555]">· reviewed by {form.reviewed_by}</span>
              )}
            </div>
          )}
        </div>
        {id && (
          <div className="flex gap-2 flex-wrap">
            {editorEmail && form.review_state !== "published" && (
              <a
                href={`mailto:${editorEmail}?subject=${encodeURIComponent("Receipts — please review draft: " + (form.title || "(untitled)"))}&body=${encodeURIComponent("Hi — a draft is awaiting review:\n\n" + (form.title || "(untitled)") + "\n\nReview at: " + PUBLIC_URL + "/admin/edit/" + id + "\n\nDrafted by: " + (form.created_by || "—") + "\nState: " + (form.review_state || "draft") + "\nCitations: " + (form.citations || []).filter((c) => c.verified).length + "/" + (form.citations || []).length + " verified" + (form.summary ? "\n\nSummary:\n" + form.summary : ""))}`}
                data-testid="notify-reviewer-btn"
              >
                <Button type="button" variant="outline" className="rounded-none border-[#FFAA00] text-[#7a5500] h-10">
                  <Mail className="w-3.5 h-3.5 mr-1.5" /> Email a reviewer
                </Button>
              </a>
            )}
            {form.review_state === "draft" && (
              <Button type="button" onClick={submitForReview} variant="outline" className="rounded-none border-[#0050FF] text-[#0050FF] h-10" data-testid="submit-review-btn">
                <Send className="w-3.5 h-3.5 mr-1.5" /> Submit for review
              </Button>
            )}
            {form.review_state !== "published" && (
              <Button type="button" onClick={publish} disabled={!canPublish} className="rounded-none bg-[#008A00] hover:bg-[#005a00] text-white h-10 disabled:opacity-40" data-testid="publish-btn"
                title={canPublish ? "Publish to public site" : "All citations must be verified first"}>
                <Globe className="w-3.5 h-3.5 mr-1.5" /> Publish
              </Button>
            )}
            {form.review_state === "published" && (
              <Button type="button" onClick={unpublish} variant="outline" className="rounded-none border-black h-10" data-testid="unpublish-btn">
                <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Unpublish
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Editorial gate banner */}
      <div className="mt-4 border border-black/15 bg-[#FFF8E1] p-4 text-sm flex items-start gap-3" data-testid="editorial-banner">
        <ShieldCheck className="w-5 h-5 mt-0.5 text-[#0050FF] shrink-0" />
        <div>
          <div className="font-medium">Editorial gate: {verifiedCount}/{cites.length} citations verified.</div>
          <div className="text-[#444] mt-0.5">
            Every citation must be reviewed by a human editor and marked verified before this entry can be published. Saving a draft never publishes.
          </div>
        </div>
      </div>

      {/* AI Helper */}
      <AIDraftAssistant url={aiUrl} setUrl={setAiUrl} loading={aiLoading} onRun={aiSummarize} />

      <form onSubmit={save} className="mt-6 bg-white border border-black/15 p-6 space-y-5" data-testid="entry-form">
        <Field label="Title *">
          <Input data-testid="form-title" value={form.title} onChange={(e) => upd("title", e.target.value)} required className="rounded-none border-black/30 h-11" />
        </Field>
        <Field label="Summary *">
          <Textarea data-testid="form-summary" value={form.summary} onChange={(e) => upd("summary", e.target.value)} required rows={3} className="rounded-none border-black/30" />
        </Field>
        <Field label="Body (optional)">
          <Textarea data-testid="form-body" value={form.body || ""} onChange={(e) => upd("body", e.target.value)} rows={5} className="rounded-none border-black/30" />
        </Field>

        <div className="grid md:grid-cols-3 gap-4">
          <SelectField label="Category *" value={form.category} onChange={(v) => upd("category", v)} options={CATEGORIES.map(c => ({ value: c.slug, label: c.label }))} testid="form-category" />
          <SelectField label="Action type" value={form.action_type} onChange={(v) => upd("action_type", v)} options={ACTION_TYPES.map(s => ({ value: s, label: formatLabel(s) }))} testid="form-action-type" />
          <SelectField label="Status" value={form.status} onChange={(v) => upd("status", v)} options={STATUSES.map(s => ({ value: s, label: formatLabel(s) }))} testid="form-status" />
          <SelectField label="Impact" value={form.impact} onChange={(v) => upd("impact", v)} options={IMPACTS.map(s => ({ value: s, label: formatLabel(s) }))} testid="form-impact" />
          <SelectField label="Level" value={form.jurisdiction_level} onChange={(v) => upd("jurisdiction_level", v)} options={JURISDICTION_LEVELS.map(s => ({ value: s, label: formatLabel(s) }))} testid="form-level" />
          <SelectField
            label={form.jurisdiction_level === "international" ? "Country" : "Jurisdiction"}
            value={form.jurisdiction}
            onChange={(v) => {
              const next = { jurisdiction: v };
              if (form.jurisdiction_level === "international") next.country = v;
              setForm((f) => ({ ...f, ...next }));
            }}
            options={(form.jurisdiction_level === "international" ? COUNTRIES : US_STATES).map(s => ({ value: s, label: s }))}
            testid="form-jurisdiction"
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Date *">
            <Input data-testid="form-date" type="date" value={form.date_action} onChange={(e) => upd("date_action", e.target.value)} required className="rounded-none border-black/30 h-11" />
          </Field>
          <Field label="Country">
            <Input data-testid="form-country" value={form.country} onChange={(e) => upd("country", e.target.value)} className="rounded-none border-black/30 h-11" />
          </Field>
        </div>

        <Field label="Tags (comma-separated)">
          <Input data-testid="form-tags" value={(form.tags || []).join(", ")} onChange={(e) => upd("tags", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="rounded-none border-black/30 h-11" />
        </Field>
        <Field label="Actors (comma-separated)">
          <Input data-testid="form-actors" value={(form.actors || []).join(", ")} onChange={(e) => upd("actors", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} className="rounded-none border-black/30 h-11" />
        </Field>

        <CitationsEditor
          citations={form.citations || []}
          entryId={id}
          onAdd={addCitation}
          onUpdate={updCitation}
          onRemove={rmCitation}
          onVerify={verifyCitation}
          onUnverify={unverifyCitation}
        />

        {/* Quick "add a single source" tool — only for already-saved entries.
            Bypasses the full form save so editors can rapidly bolster sources. */}
        <QuickAddSource entryId={id} onAdded={() => reload()} />

        <div className="pt-4 border-t border-black/10 flex gap-3 justify-between flex-wrap">
          <div className="text-xs text-[#555] font-mono-plex uppercase tracking-wider">
            {id ? "Saving updates the draft. Editing citations on a published entry sends it back to pending review." : "Saving creates a draft."}
          </div>
          <div className="flex gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/admin")} className="rounded-none border-black/40 h-11">Cancel</Button>
            <Button type="submit" disabled={loading} className="rounded-none bg-black text-white h-11" data-testid="form-save-btn">
              {(() => {
                if (loading) return "Saving…";
                return id ? "Save changes" : "Create draft";
              })()}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="overline mb-1.5 block">{label}</span>
      {children}
    </label>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="overline mb-1.5 block">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="rounded-none border-black/30 h-11"><SelectValue /></SelectTrigger>
        <SelectContent className="rounded-none max-h-72">
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </label>
  );
}
